import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import SourceCard from './SourceCard';

/**
 * Renders thesis markdown with [src:ID] citations as clickable numbered chips.
 * Left column: markdown content with colored [N] citation chips.
 * Right column: all cited source cards in citation order, flowing continuously.
 *
 * Citation numbering is derived from `citationMap` — the ordered list of unique
 * source IDs by first appearance in the markdown. Both the [N] chips and the
 * source cards use this same list, guaranteeing 1:1 mapping.
 */

const CITE_START = '\u2060\u200B';
const CITE_END = '\u200B\u2060';


export default function ThesisMarkdown({ markdown, sources, onCitationClick, activeFilter, highlightedSource }) {
  // ── Source lookup by ID ───────────────────────────────────────────────
  const sourceMap = useMemo(() => {
    const m = {};
    (sources || []).forEach(s => { m[s.id] = s; });
    return m;
  }, [sources]);

  // ── Pass 1: scan markdown for all [src:ID] citations ──────────────────
  // Only include IDs that exist in the sources array so every [N] chip
  // has a matching card [N]. Missing sources get their [src:ID] tag
  // silently removed from the rendered text.
  const { processedMarkdown, citationMap } = useMemo(() => {
    const allIds = [];
    const seen = new Set();
    const regex = /\[src:([^\]]+)\]/g;

    let match;
    while ((match = regex.exec(markdown)) !== null) {
      const id = match[1];
      if (!seen.has(id)) { seen.add(id); allIds.push(id); }
    }

    // Only sources that actually exist get a citation number
    const validMap = allIds.filter(id => sourceMap[id]);

    const processed = markdown.replace(regex, (_, id) => {
      const idx = validMap.indexOf(id);
      if (idx === -1) return ''; // source missing from data — remove tag
      return `${CITE_START}CITE:${idx}:${id}${CITE_END}`;
    });

    return { processedMarkdown: processed, citationMap: validMap };
  }, [markdown, sourceMap]);

  // ── Build the right-column card list directly from citationMap ────────
  // Guaranteed 1:1: citationMap only contains valid IDs.
  const allSourceCards = useMemo(() => {
    return citationMap.map((id, idx) => ({
      ...sourceMap[id],
      _citationIndex: idx,
    }));
  }, [citationMap, sourceMap]);

  // ── Citation injection into text nodes ────────────────────────────────
  function injectCitations(children) {
    if (!children) return children;
    return React.Children.map(children, child => {
      if (typeof child !== 'string') {
        if (child?.props?.children) {
          return React.cloneElement(child, {}, injectCitations(child.props.children));
        }
        return child;
      }

      const parts = child.split(
        new RegExp(`${escapeRegex(CITE_START)}CITE:(\\d+):(.+?)${escapeRegex(CITE_END)}`)
      );
      if (parts.length === 1) return child;

      const result = [];
      for (let i = 0; i < parts.length; i++) {
        if (i % 3 === 0) {
          if (parts[i]) result.push(parts[i]);
        } else if (i % 3 === 1) {
          const idx = parseInt(parts[i]);
          const id = parts[i + 1];
          const num = idx + 1;
          const source = sourceMap[id];
          result.push(
            <span
              key={`cite-${id}-${i}`}
              className="thesis-cite"
              title={source?.title || id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCitationClick?.(id);
              }}
            >
              [{num}]
            </span>
          );
          i++; // skip the id part
        }
      }
      return result;
    });
  }

  // ── Custom markdown renderers ─────────────────────────────────────────
  const components = useMemo(() => ({
    h1: ({ children }) => <h2 className="thesis-h1">{children}</h2>,
    h2: ({ children }) => <h2 className="thesis-h2">{injectCitations(children)}</h2>,
    h3: ({ children }) => <h3 className="thesis-h3">{injectCitations(children)}</h3>,
    p:  ({ children }) => <p className="thesis-p">{injectCitations(children)}</p>,
    li: ({ children }) => <li className="thesis-li">{injectCitations(children)}</li>,
    strong: ({ children }) => <strong>{injectCitations(children)}</strong>,
    em: ({ children }) => <em>{injectCitations(children)}</em>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [sourceMap, onCitationClick]);

  return (
    <div className="thesis-two-col">
      <div className="thesis-two-col__content">
        <div className="thesis-markdown">
          <ReactMarkdown components={components}>
            {processedMarkdown}
          </ReactMarkdown>
        </div>
      </div>
      <div className="thesis-two-col__sources">
        {allSourceCards.map(s => {
          const dimmed = activeFilter && s.source_type !== activeFilter;
          return (
            <SourceCard
              key={s.id}
              source={s}
              index={s._citationIndex}
              highlighted={highlightedSource === s.id}
              compact
              dimmed={dimmed}
            />
          );
        })}
      </div>
    </div>
  );
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
